import * as DOMPurify from 'dompurify';

import {useEffect, useMemo, useState} from 'react';

import {MergedProduct} from '../context/types';
import {Panel} from './Panel';
import {ShoppingType} from '~/gql/generated';

interface descriptionNode {
  headline: string;
  copy: string;
}

export function splitDescription(
  des: string | undefined | null,
  returns: string,
  supportEmail: string,
  shoppingType: string,
) {
  let result: descriptionNode[] = [];

  des = des ?? '';
  // only matches headlines with no attributes
  const headlines = des.trim().match(/<h(.)>.*?<\/h\1>/g);
  if (
    headlines &&
    [
      ShoppingType.Full,
      ShoppingType.FirstParty,
      ShoppingType.Synthetic,
    ].includes(shoppingType)
  ) {
    result = (headlines || [])
      .reduce((res, headline, index) => {
        const previousNode = res[index - 1];
        const previous = previousNode ? previousNode.copy : des;
        const split = (previous ?? '').split(headline);
        const node = {
          headline: stripAllHtmlWrapperTags(headline),
          copy: split[1],
        };
        res.push(node);
        if (previousNode) {
          previousNode.copy = split[0];
        }

        return res;
      }, [] as {headline: string; copy: string}[])
      .filter(({copy}) => copy.trim().length > 0);
  } else if (des.trim().length > 0) {
    result.push({
      headline: 'From The Brand',
      copy: des,
    });
  }
  // returns/support policy section - feel free to remove
  if (returns) {
    result.push({
      headline: 'Returns',
      copy: `${returns}${
        supportEmail
          ? `<a class="block" href="mailto:${supportEmail}">${supportEmail}</a>`
          : ''
      }`,
    });
  }

  return result;
}

export const BrandPanels = ({
  product,
  isFirstOpen,
}: {
  product: MergedProduct;
  isFirstOpen: boolean;
}) => {
  const split = useMemo(
    () =>
      splitDescription(
        product.brandDescription,
        product.returnPolicy,
        product.supportEmail,
        product.shoppingType,
      ),
    [product.brandDescription, product.returnPolicy, product.supportEmail],
  );

  const [content, setContent] = useState(split);

  useEffect(() => {
    setContent(
      (content || []).map((item) => ({
        ...item,
        copy: DOMPurify.sanitize(item.copy, {
          ALLOWED_TAGS: ['p', 'div', 'span', 'ul', 'li', '#text', 'a'],
          KEEP_CONTENT: true,
        }),
      })),
    );
  }, []);

  return (
    <>
      {content.map(({headline, copy}, index) => (
        <Panel
          key={headline}
          headline={headline}
          copy={copy}
          isOpen={isFirstOpen && index === 0}
          shouldTruncate={isFirstOpen && index === 0 && copy.length > 500}
        />
      ))}
    </>
  );
};

export function stripAllHtmlWrapperTags(html: string) {
  return html.replace(/<\/?[^>]+(>|$)/g, '');
}
